/**
 * Root application component: defines all top-level client routes via react-router-dom.
 * Routes: "/" (LoggedOutHome), "/bounties/*" (BrowseCollection), "/search" (SearchPage),
 * "/signUpLogin", "/verifyEmail", "/account/*" (UserHomepage), "/admin/*" (AdminHomepage),
 * "/email-unsubscribe/:token". Auth-gated routes are wrapped in components/ProtectedRoute.
 * Wraps the app in ErrorProvider and UserProvider contexts, renders the fixed TopNavBar,
 * wires axios auth handlers (lib/axios.ts) and cross-tab auth sync (hooks/useAuthSync).
 * Rendered inside BrowserRouter by app/index.tsx.
 */
import TopNavBar from "@/components/TopNavBar";
import LoggedOutHome from "@/pages/logged_out/LoggedOutHome";
import { Routes, Route } from "react-router-dom";
import SignUpOrLogin from "@/pages/user/SignUpOrLogIn";
import { UserHomepage } from "@/pages/user/UserHomepage";
import ProtectedRoute from "@/components/ProtectedRoute";
import VerifyEmail from "@/pages/user/VerifyEmail";
import { BrowseCollection } from "@/pages/bounty/BrowseCollection";
import { enableMapSet } from "immer";
import { useUserDispatch } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { setupAuthHandlers } from "@/lib/axios";
import { useAuthSync } from "@/hooks/useAuthSync";
import EmailUnsubscribe from "@/pages/logged_out/EmailUnsubscribe";
import { UserProvider } from "@/contexts/UserContext";
import { ErrorProvider } from "@/contexts/ErrorContext";
import { AdminHomepage } from "@/pages/admin/AdminHomepage";
import SearchPage from "@/pages/search/SearchPage";

// Enable the MapSet plugin
enableMapSet();

const AppContent: React.FC = () => {
    const dispatch = useUserDispatch();
    const navigate = useNavigate();

    // Set up auth sync to keep frontend state in sync with backend
    useAuthSync();

    useEffect(() => {
        setupAuthHandlers(dispatch!, navigate);
    }, [dispatch, navigate]);

    return (
        <>
            <TopNavBar />
            <div className="overflow-x-hidden">
                <main className="flex flex-col min-h-screen h-screen w-full pt-16 bg-slate-50">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute component={LoggedOutHome} requiresLogin={false} />
                            }
                        />
                        <Route path="/bounties/*" element={<BrowseCollection />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route
                            path="/signUpLogin"
                            element={
                                <ProtectedRoute component={SignUpOrLogin} requiresLogin={false} />
                            }
                        />
                        <Route
                            path="/verifyEmail"
                            element={
                                <ProtectedRoute component={VerifyEmail} requiresLogin={true} />
                            }
                        />
                        <Route
                            path="/account/*"
                            element={
                                <ProtectedRoute component={UserHomepage} requiresLogin={true} />
                            }
                        />
                        <Route path="/email-unsubscribe/:token" element={<EmailUnsubscribe />} />
                        <Route
                            path="/admin/*"
                            element={
                                <ProtectedRoute
                                    component={AdminHomepage}
                                    requiresLogin={true}
                                    requiresAdmin={true}
                                />
                            }
                        />
                        <Route path="*" element={<div>404: Page Not Found</div>} />
                    </Routes>
                </main>
            </div>
        </>
    );
};

const App: React.FC = () => {
    return (
        <ErrorProvider>
            <UserProvider>
                <AppContent />
            </UserProvider>
        </ErrorProvider>
    );
};

export default App;
